import { ChallengeDifficultyBadge, ChallengeTypeBadge } from '@/components/challenges';
import type { ChallengeTypeValue } from '@/components/challenges/challengeKinds';
import { VisibilityBadge } from '@/components/shared';
import { renderDateCell, renderNumberCell } from '@/lib/listCells';
import { Badge, type CrudConfig } from '@startupquickstart/frontend';

/**
 * Challenge type matching the API schema. Derived from the single source of
 * truth in `challengeKinds.ts` (`CHALLENGE_TYPES`).
 */
export type ChallengeType = ChallengeTypeValue;

export type ChallengeDifficulty = 'easy' | 'medium' | 'hard';
export type Visibility = 'private' | 'unlisted' | 'public';
export type AnswerType = 'text' | 'photo' | 'video' | 'true_false' | 'multiple_choice' | 'fill_in_the_blank' | 'puzzle';

export interface Challenge {
  id: string;
  organizationId: string;
  name: string;
  description?: string | null;
  clue: string;
  hint?: string | null;
  hintCost: number;
  aiContext?: string | null;
  // Answer is nullable for photo/video types which use AI validation
  answer?: string | null;
  alternateAnswers: string[];
  choices: string[];
  points: number;
  difficulty: ChallengeDifficulty;
  type: ChallengeType;
  zone?: string | null;
  latitude?: number | null;
  /** Map-eligibility override; 'auto' defers to the challenge type. */
  showOnMap?: boolean | null;
  /** Max video recording length in seconds; null uses the app default. */
  videoMaxDurationSeconds?: number | null;
  longitude?: number | null;
  answerType: AnswerType;
  tags: string[];
  imageUrl?: string | null;
  videoUrl?: string | null;
  audioUrl?: string | null;
  voiceoverId?: string | null;
  /** Server-computed: clue changed since the linked voiceover was generated. */
  voiceoverStale?: boolean;
  /** Per-type minigame config (memory_match, slot_machine, etc.). */
  minigameConfig?: Record<string, unknown> | null;
  /** Default access code (passcode) + prompt; copies into each event. */
  accessCode?: string | null;
  accessCodeMessage?: string | null;
  puzzleGridSize: number;
  isTimed: boolean;
  visibility: Visibility;
  createdAt: string;
  updatedAt: string;
  organization?: {
    id: string;
    name: string;
  } | null;
  /** Managed greater area (city/region) this challenge belongs to, if any. */
  greaterAreaId?: string | null;
  /** The linked greater area, included on list/detail responses. */
  greaterArea?: {
    id: string;
    name: string;
    slug: string;
    color?: string | null;
  } | null;
}

export const challengeCrudConfig: CrudConfig<Challenge> = {
  entityName: 'Challenge',
  entityNamePlural: 'Challenges',

  endpoints: {
    list: '/challenges',
    single: (id: string) => `/challenges/${id}`,
    create: '/challenges',
    update: (id: string) => `/challenges/${id}`,
    delete: (id: string) => `/challenges/${id}`,
  },

  fields: [
    {
      key: 'name',
      label: 'Name',
      type: 'text',
      required: true,
      placeholder: 'Enter challenge name',
      validation: {
        min: 1,
        max: 200,
      },
    },
    {
      key: 'type',
      label: 'Type',
      type: 'select',
      required: true,
      defaultValue: 'riddle',
      options: [
        { label: 'Trivia', value: 'riddle' },
        { label: 'Counting', value: 'counting' },
        { label: 'Exploration', value: 'exploration' },
        { label: 'Search', value: 'search' },
        { label: 'Observation', value: 'observation' },
        { label: 'Photo', value: 'photo' },
        { label: 'Video', value: 'video' },
        { label: 'Finale', value: 'finale' },
        { label: 'Puzzle', value: 'puzzle' },
      ],
      format: (value: string) => {
        if (!value) return null;
        return <ChallengeTypeBadge type={value as ChallengeType} />;
      },
    },
    {
      key: 'difficulty',
      label: 'Difficulty',
      type: 'select',
      required: true,
      defaultValue: 'medium',
      options: [
        { label: 'Easy', value: 'easy' },
        { label: 'Medium', value: 'medium' },
        { label: 'Hard', value: 'hard' },
      ],
      format: (value: string) => {
        if (!value) return null;
        return <ChallengeDifficultyBadge difficulty={value as ChallengeDifficulty} />;
      },
    },
    {
      key: 'visibility',
      label: 'Visibility',
      type: 'select',
      required: true,
      defaultValue: 'private',
      options: [
        { label: 'Private', value: 'private' },
        { label: 'Unlisted', value: 'unlisted' },
        { label: 'Public', value: 'public' },
      ],
      format: (value: string) => {
        if (!value) return null;
        return <VisibilityBadge visibility={value as Visibility} />;
      },
    },
    {
      // Read-only display column: the challenge's greater area (city), if any.
      key: 'greaterArea',
      label: 'Area',
      type: 'text',
      required: false,
      format: (value: Challenge['greaterArea']) =>
        value ? <Badge variant="secondary">{value.name}</Badge> : null,
    },
    {
      key: 'points',
      label: 'Points',
      type: 'number',
      required: true,
      defaultValue: 100,
      placeholder: 'Enter points value',
      align: 'right',
      format: renderNumberCell,
    },
    {
      key: 'clue',
      label: 'Clue',
      type: 'richtext',
      required: true,
      placeholder: 'Enter the clue for this challenge',
    },
    {
      key: 'hint',
      label: 'Hint',
      type: 'richtext',
      required: false,
      placeholder: 'Enter an optional hint',
    },
    {
      key: 'hintCost',
      label: 'Hint Cost',
      type: 'number',
      required: false,
      defaultValue: 0,
      placeholder: 'Points deducted for using hint',
      helpText: 'Set to 0 for free hints',
    },
    {
      key: 'answer',
      label: 'Answer',
      type: 'text',
      required: false, // Conditional: required for non-photo/video types
      placeholder: 'Enter the correct answer',
      helpText: 'Not required for photo/video challenges (use AI Context instead)',
    },
    {
      key: 'alternateAnswers',
      label: 'Alternate Answers',
      type: 'text',
      required: false,
      placeholder: 'Enter alternate answers (comma separated)',
      helpText: 'Multiple acceptable answers separated by commas',
    },
    {
      key: 'aiContext',
      label: 'AI Validation Criteria',
      type: 'richtext',
      required: false, // Conditional: required for photo/video types
      placeholder: 'Describe what the photo/video must show...',
      helpText: 'Required for photo/video challenges. Describe what the AI should look for to validate submissions.',
    },
    {
      key: 'zone',
      label: 'Zone',
      type: 'text',
      required: false,
      placeholder: 'Enter zone name',
    },
    {
      key: 'latitude',
      label: 'Latitude',
      type: 'number',
      required: false,
    },
    {
      key: 'longitude',
      label: 'Longitude',
      type: 'number',
      required: false,
    },
    {
      key: 'videoMaxDurationSeconds',
      label: 'Max Video Length',
      type: 'select',
      required: false,
      options: [
        { label: 'Default (1 minute)', value: '' },
        { label: '10 seconds', value: '10' },
        { label: '15 seconds', value: '15' },
        { label: '30 seconds', value: '30' },
        { label: '1 minute', value: '60' },
      ],
      helpText:
        'Only applies to video answers. Capped at 1 minute — longer clips stop fitting under the 50MB upload limit.',
    },
    {
      key: 'showOnMap',
      label: 'Show on Map',
      type: 'select',
      required: false,
      defaultValue: 'auto',
      options: [
        { label: 'Automatic (by challenge type)', value: 'auto' },
        { label: 'Always show', value: 'show' },
        { label: 'Never show', value: 'hide' },
      ],
      helpText:
        'Automatic keeps trivia, puzzles and minigames off the map and pins everything else. Override for a photo challenge teams can do anywhere, or trivia read off a plaque.',
    },
    {
      key: 'answerType',
      label: 'Answer Type',
      type: 'select',
      required: true,
      defaultValue: 'text',
      options: [
        { label: 'Text', value: 'text' },
        { label: 'Photo', value: 'photo' },
        { label: 'Video', value: 'video' },
        { label: 'True / False', value: 'true_false' },
        { label: 'Multiple Choice', value: 'multiple_choice' },
        { label: 'Fill in the Blank', value: 'fill_in_the_blank' },
        { label: 'Puzzle', value: 'puzzle' },
      ],
      format: (value: string) => {
        const labels: Record<string, string> = {
          text: 'Text',
          photo: 'Photo',
          video: 'Video',
          true_false: 'True / False',
          multiple_choice: 'Multiple Choice',
          fill_in_the_blank: 'Fill in the Blank',
          puzzle: 'Puzzle',
        };
        return labels[value] || value;
      },
      helpText: 'What participants must submit to complete this challenge',
    },
    {
      key: 'imageUrl',
      label: 'Image URL',
      type: 'text',
      required: false,
      placeholder: 'Enter image URL',
    },
    {
      key: 'videoUrl',
      label: 'Video URL',
      type: 'text',
      required: false,
      placeholder: 'Enter video URL',
    },
    {
      key: 'puzzleGridSize',
      label: 'Puzzle Grid Size',
      type: 'select',
      required: false,
      defaultValue: 3,
      options: [
        { label: '3x3 (Easy)', value: 3 },
        { label: '4x4 (Hard)', value: 4 },
      ],
      helpText: 'Grid size for puzzle challenges (3x3 or 4x4)',
    },
    {
      key: 'isTimed',
      label: 'Timed Challenge',
      type: 'boolean',
      required: false,
      defaultValue: false,
      helpText: 'Track how long teams take to complete this challenge',
    },
    {
      key: 'createdAt',
      label: 'Created',
      type: 'datetime',
      readonly: true,
      format: renderDateCell,
    },
    {
      key: 'updatedAt',
      label: 'Updated',
      type: 'datetime',
      readonly: true,
      format: renderDateCell,
    },
  ],

  listView: {
    fields: ['name', 'type', 'greaterArea', 'difficulty', 'points', 'visibility', 'updatedAt'],
    // Phones keep the two leading columns; the rest is desktop
    // detail (hideOnMobile) so the table never scrolls sideways.
    columns: [
      { field: 'name' },
      { field: 'type' },
      { field: 'greaterArea', hideOnMobile: true },
      { field: 'difficulty', hideOnMobile: true },
      { field: 'points', hideOnMobile: true },
      { field: 'visibility', hideOnMobile: true },
      { field: 'updatedAt', hideOnMobile: true },
    ],
    searchableFields: ['name', 'clue', 'answer', 'zone'],
    sortableFields: ['name', 'type', 'difficulty', 'points', 'visibility', 'createdAt', 'updatedAt'],
    defaultSort: {
      field: 'updatedAt',
      direction: 'desc',
    },
    itemsPerPage: 10,
    // Saved lists + the filter panel are intentionally disabled for now
    // (search + column sorting only). Filter definitions live in git history
    // (PR #369-era configs) — restore them here to re-enable both.
    filters: [],
    actions: {
      create: { enabled: true },
      view: { enabled: true },
      edit: { enabled: true },
      delete: { enabled: true },
    },
  },

  detailView: {
    sections: [
      {
        title: 'Challenge Details',
        description: 'Core challenge information',
        fields: ['type', 'difficulty', 'points', 'visibility', 'answerType', 'puzzleGridSize', 'isTimed'],
        defaultExpanded: true,
      },
      {
        title: 'Content',
        description: 'Challenge clue, hint, and answer',
        fields: ['clue', 'hint', 'hintCost', 'answer', 'alternateAnswers', 'aiContext'],
        defaultExpanded: true,
      },
      {
        title: 'Location',
        description: 'Location information',
        fields: ['zone', 'latitude', 'longitude', 'showOnMap'],
        defaultExpanded: true,
      },
      {
        title: 'Media',
        description: 'Images and videos',
        fields: ['imageUrl', 'videoUrl', 'videoMaxDurationSeconds'],
        defaultExpanded: true,
      },
      {
        title: 'System Information',
        description: 'Timestamps and metadata',
        fields: ['createdAt', 'updatedAt'],
        defaultExpanded: false,
      },
    ],
  },

  form: {
    sections: [
      {
        title: 'Challenge Details',
        description: 'Basic information about the challenge',
        fields: ['name', 'type', 'difficulty', 'points', 'visibility', 'answerType', 'puzzleGridSize', 'isTimed'],
        columns: 2,
      },
      {
        title: 'Content',
        description: 'The clue, hint, and answer for this challenge',
        fields: ['clue', 'hint', 'hintCost', 'answer', 'alternateAnswers', 'aiContext'],
        columns: 1,
      },
      {
        title: 'Location',
        description: 'Optional location information',
        fields: ['zone', 'latitude', 'longitude', 'showOnMap'],
        columns: 2,
      },
      {
        title: 'Media',
        description: 'Images and videos for the challenge',
        fields: ['imageUrl', 'videoUrl', 'videoMaxDurationSeconds'],
        columns: 2,
      },
      // The greater area is edited via a dropdown in the challenge editor's
      // Location section (a plain FK field), not as a config-driven form field.
    ],
    validation: {
      name: { required: true, min: 1, max: 200 },
      clue: { required: true },
      // answer is conditionally required (for non-photo/video types)
      // aiContext is conditionally required (for photo/video types)
      type: { required: true },
      difficulty: { required: true },
      points: { required: true },
    },
  },
};
